/**
 * Login Page - Modern Split-Screen Layout (Supabase-Style)
 *
 * WCAG 2.2 AA compliant
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Input } from '@/components/v2/ui/Input';
import { Button } from '@/components/v2/ui/Button';
import { Alert } from '@/components/v2/ui/Alert';
import { Loading } from '@/components/v2/ui/Loading';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type LoginMode = 'magic' | 'password';

export function LoginRedesign() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, sendMagicLink, verifyMagicLink, isAuthenticated } = useSupabaseAuth();
  const { theme } = useTheme();
  const logoPath = theme === 'dark' ? '/images/logo-dark.svg' : '/images/logo-white.svg';

  const [mode, setMode] = useState<LoginMode>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [error, setError] = useState('');

  // Redirect wenn bereits authentifiziert
  useEffect(() => {
    if (isAuthenticated) {
      const next = searchParams.get('next') || '/v2/dashboard';
      navigate(next, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const handleVerifyMagicLink = useCallback(async (token: string, email: string) => {
    setIsSubmitting(true);
    setError('');
    try {
      const result = await verifyMagicLink(token, email);
      if (result.success) {
        toast.success(t('toast.success.loginSuccess'));
        navigate('/v2/dashboard');
      } else {
        setError(result.error || t('errors.verificationFailed'));
        setIsSubmitting(false);
      }
    } catch {
      setError(t('errors.generic'));
      setIsSubmitting(false);
    }
  }, [t, verifyMagicLink, navigate]);

  // Magic Link Verifizierung aus URL
  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');

    if (token && emailParam) {
      handleVerifyMagicLink(token, emailParam);
    }
  }, [searchParams, handleVerifyMagicLink]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (mode === 'magic') {
        const result = await sendMagicLink(email);
        if (result.success) {
          setMagicLinkSent(true);
          toast.success(t('toast.success.magicLinkSent'));
        } else {
          setError(result.error || t('toast.error.magicLink'));
        }
      } else {
        const result = await login(email, password);
        if (result.success) {
          toast.success(t('toast.success.loginSuccess'));
          navigate('/v2/dashboard');
        } else {
          setError(result.error || t('auth.loginFailed'));
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="login-form-container flex-1 flex flex-col justify-between px-12 py-8 lg:px-24 bg-white dark:bg-neutral-950">
        {/* Logo */}
        <div className="login-logo">
          <Link to="/" className="flex items-center">
            <img
              src={logoPath}
              alt="GOKI Logo"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        {/* Form Content */}
        <div className="login-form-content max-w-sm w-full mx-auto space-y-8">
          {/* Headline */}
          <div className="login-form-header space-y-2">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
              {t('auth.welcomeBack')}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t('auth.signIn')}
            </p>
          </div>

          {/* Mode Toggle - Magic Link / Password */}
          <div className="login-mode-toggle">
            <div className="relative flex p-1 bg-neutral-100 dark:bg-neutral-800 rounded-xl">
              {/* Animated Background */}
              <div
                className={cn(
                  'absolute top-1 bottom-1 bg-white dark:bg-neutral-700 rounded-lg shadow-sm transition-all duration-300 ease-out',
                  mode === 'magic' ? 'left-1 right-[50%]' : 'left-[50%] right-1'
                )}
              />
              <button
                type="button"
                onClick={() => setMode('magic')}
                className={cn(
                  'relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-colors z-10',
                  mode === 'magic'
                    ? 'text-neutral-900 dark:text-white'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                Magic Link
              </button>
              <button
                type="button"
                onClick={() => setMode('password')}
                className={cn(
                  'relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-colors z-10',
                  mode === 'password'
                    ? 'text-neutral-900 dark:text-white'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <KeyRound className="w-4 h-4" aria-hidden="true" />
                Passwort
              </button>
            </div>
          </div>

          {/* Magic Link Success Message */}
          {magicLinkSent && mode === 'magic' && (
            <Alert variant="success" className="login-success">
              {t('auth.magicLinkSentInfo')}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="error" className="login-error">
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div className="login-form-email">
              <Input
                id="email"
                type="email"
                label="E-Mail"
                placeholder="du@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            {/* Password Input (smooth height transition) */}
            <div
              className="grid transition-all duration-300 ease-out"
              style={{
                gridTemplateRows: mode === 'password' ? '1fr' : '0fr',
              }}
            >
              <div className="overflow-hidden">
                <div className="login-form-password space-y-2">
                  <Input
                    id="password"
                    type="password"
                    label="Passwort"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={mode === 'password'}
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/v2/reset-password"
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      Passwort vergessen?
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              className="w-full login-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loading size="sm" />
              ) : mode === 'magic' ? (
                t('auth.signInWithMagicLink')
              ) : (
                t('auth.signIn')
              )}
            </Button>
          </form>

          {/* Register Link */}
          <div className="login-register-link text-center">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Noch kein Account?{' '}
              <Link
                to="/v2/register"
                className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                {t('common.register')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="login-footer text-center text-sm text-neutral-500 dark:text-neutral-600 space-x-4">
          <Link to="/impressum" className="hover:text-neutral-700 dark:hover:text-neutral-400 transition-colors">
            Impressum
          </Link>
          <span>|</span>
          <Link to="/datenschutz" className="hover:text-neutral-700 dark:hover:text-neutral-400 transition-colors">
            Datenschutz
          </Link>
          <span>|</span>
          <Link to="/agb" className="hover:text-neutral-700 dark:hover:text-neutral-400 transition-colors">
            AGB
          </Link>
        </footer>
      </div>

      {/* Right Side - Hero/Testimonial (Desktop only) */}
      <div className="login-hero hidden lg:flex flex-1 flex-col items-center justify-center p-16 bg-gradient-to-br from-primary-50 to-primary-50 dark:from-primary-950 dark:to-primary-950 relative overflow-hidden">
        {/* Decorative Gradient Orbs */}
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -left-32 w-96 h-96 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />

        {/* Testimonial Card */}
        <div className="login-testimonial-card relative z-10 max-w-lg bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-2xl shadow-2xl p-8 space-y-6">
          {/* Quote */}
          <blockquote className="login-testimonial-quote text-xl leading-relaxed text-neutral-800 dark:text-neutral-200">
            "{t('auth.testimonial')}"
          </blockquote>

          {/* Author */}
          <div className="login-testimonial-author flex items-center gap-4">
            {/* Avatar (Gradient Circle) */}
            <div className="login-testimonial-avatar w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-500 flex items-center justify-center shadow-lg">
              <span className="text-white text-xl font-semibold" aria-hidden="true">M</span>
            </div>

            {/* Name + Position */}
            <div className="login-testimonial-info">
              <div className="font-semibold text-neutral-900 dark:text-white">
                Max Mustermann
              </div>
              <div className="text-sm text-neutral-600 dark:text-neutral-400">
                CEO, MusterShop GmbH
              </div>
            </div>
          </div>
        </div>

        {/* Footer Copyright (Desktop) */}
        <div className="login-hero-footer absolute bottom-8 text-sm text-neutral-600 dark:text-neutral-500">
          © 2026 GOKI Image
        </div>
      </div>
    </div>
  );
}

export default LoginRedesign;
